import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// Create styles
const styles = StyleSheet.create({
  page: {
    backgroundColor: '#FFFFFF',
    padding: 30,
  },
  header: {
    marginBottom: 20,
    borderBottom: '1px solid #888',
    paddingBottom: 10,
  },
  title: {
    fontSize: 24,
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
    color: '#555',
    marginBottom: 10,
  },
  questionSection: {
    marginBottom: 30,
  },
  questionHeader: {
    backgroundColor: '#f0f0f0',
    padding: 10,
    marginBottom: 10,
    borderRadius: 5,
  },
  questionNumber: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  questionText: {
    fontSize: 12,
    marginBottom: 10,
    lineHeight: 1.4,
  },
  optionsContainer: {
    marginLeft: 20,
    marginBottom: 10,
  },
  option: {
    fontSize: 12,
    marginBottom: 5,
    lineHeight: 1.4,
  },
  correctOption: {
    fontSize: 12,
    marginBottom: 5,
    fontWeight: 'bold',
    color: '#007700',
    lineHeight: 1.4,
  },
  divider: {
    borderBottom: '1px solid #ddd',
    marginVertical: 10,
  },
  solutionHeader: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 5,
    marginTop: 5,
  },
  solutionText: {
    fontSize: 12,
    backgroundColor: '#f5f8ff',
    padding: 10,
    borderRadius: 5,
    lineHeight: 1.4,
  },
  subjectChip: {
    fontSize: 10,
    padding: '3 8',
    borderRadius: 5,
    color: 'white',
    display: 'inline',
    marginBottom: 5,
  },
  biologyChip: {
    backgroundColor: '#4caf50',
  },
  chemistryChip: {
    backgroundColor: '#f50057',
  },
  physicsChip: {
    backgroundColor: '#1976d2',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 10,
    color: '#888',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    fontSize: 10,
    color: '#888',
  },
});

// Helper function to ensure solution text is always a string
const getSolutionText = (solution) => {
  if (!solution) return "";
  if (typeof solution === 'string') return solution;
  if (typeof solution === 'object') {
    // If solution is an object with a text property, use that
    if (solution.text) return solution.text;
    // Otherwise, convert the object to a string representation
    return JSON.stringify(solution);
  }
  // Default case, convert to string
  return String(solution);
};

// PDF Document component
const PdfDocument = ({ questions }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.title}>NEET Question Paper Solutions</Text>
        <Text style={styles.subtitle}>
          Generated on {new Date().toLocaleDateString()} | Total Questions: {questions.length}
        </Text>
      </View>
      
      {questions.map((question, index) => (
        <View key={question.id} style={styles.questionSection} break={index !== 0 && index % 2 === 0}>
          <View style={styles.questionHeader}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
              <Text style={styles.questionNumber}>Question {question.questionNumber}</Text>
              <View style={[
                styles.subjectChip, 
                question.subject === 'Biology' ? styles.biologyChip : 
                question.subject === 'Chemistry' ? styles.chemistryChip : styles.physicsChip
              ]}>
                <Text>{question.subject}</Text>
              </View>
            </View>
            <Text style={styles.questionText}>{question.text}</Text>
          </View>
          
          <View style={styles.optionsContainer}>
            {Object.entries(question.options).map(([key, value]) => (
              <Text 
                key={key} 
                style={question.solution && key === question.correctOption ? styles.correctOption : styles.option}
              >
                {key}. {value}
              </Text>
            ))}
          </View>
          
          {question.solution && (
            <>
              <View style={styles.divider} />
              <Text style={styles.solutionHeader}>Solution:</Text>
              <Text style={styles.solutionText}>
                {getSolutionText(question.solution)}
              </Text>
            </>
          )}
        </View>
      ))}
      
      <Text style={styles.footer}>
        Generated by NEET Paper Solver App
      </Text>
      <Text 
        style={styles.pageNumber} 
        render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} 
        fixed 
      />
    </Page>
  </Document>
);

export default PdfDocument;
